import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DatePicker,
  Drawer,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import {
  fetchAdminUsageHistory,
  fetchUsageByUser,
  type AdminUsageRecord,
  type UsageByUserRow,
} from '../../api/usageAdminApi';
import { getUser, searchUsers, type UserBrief } from '../../api/userSearchApi';
import { formatTokensCompact, formatTokensExact } from './formatTokens';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const BIZ_OPTIONS = [
  { value: 'cengliu', label: 'cengliu' },
  { value: 'qingfan', label: 'qingfan' },
  { value: 'aisolve', label: 'aisolve' },
];

function defaultRange(): [Dayjs, Dayjs] {
  return [dayjs().subtract(29, 'day'), dayjs()];
}

function toDateOnly(d: Dayjs): string {
  return d.format('YYYY-MM-DD');
}

function displayName(user: UserBrief | undefined, userId: string): string {
  if (!user) return userId;
  return user.displayName || user.name || user.username || userId;
}

export function AiUsagePage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange);
  const [bizCode, setBizCode] = useState<string | undefined>();
  const [filterUserId, setFilterUserId] = useState<string | undefined>();
  const [userOptions, setUserOptions] = useState<UserBrief[]>([]);
  const [userCache, setUserCache] = useState<Map<string, UserBrief>>(new Map());
  const [userSearching, setUserSearching] = useState(false);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<UsageByUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRows, setDetailRows] = useState<AdminUsageRecord[]>([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(50);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const from = toDateOnly(range[0]);
  const to = toDateOnly(range[1]);

  const mergeUsers = useCallback((users: UserBrief[]) => {
    setUserCache((prev) => {
      const next = new Map(prev);
      for (const u of users) next.set(u.id, u);
      return next;
    });
  }, []);

  const userCacheRef = useRef(userCache);
  userCacheRef.current = userCache;

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchUsageByUser({
        from,
        to,
        bizCode,
        userId: filterUserId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setRows(result.items);
      setTotal(result.total);

      const missing = result.items
        .map((r) => r.userId)
        .filter((id) => !userCacheRef.current.has(id));
      await Promise.all(
        missing.map(async (id) => {
          const u = await getUser(id);
          if (u) mergeUsers([u]);
        }),
      );
    } catch (err) {
      message.error(err instanceof Error ? err.message : '加载用量汇总失败');
    } finally {
      setLoading(false);
    }
  }, [from, to, bizCode, filterUserId, page, pageSize, mergeUsers]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const loadDetail = useCallback(
    async (userId: string, nextPage: number, nextPageSize: number) => {
      setDetailLoading(true);
      try {
        const result = await fetchAdminUsageHistory(userId, {
          from,
          to,
          bizCode,
          limit: nextPageSize,
          offset: (nextPage - 1) * nextPageSize,
        });
        setDetailRows(result.items);
        setDetailTotal(result.total);
      } catch (err) {
        message.error(err instanceof Error ? err.message : '加载用量明细失败');
      } finally {
        setDetailLoading(false);
      }
    },
    [from, to, bizCode],
  );

  const openDetail = (userId: string) => {
    setDetailUserId(userId);
    setDetailPage(1);
    setDrawerOpen(true);
    void loadDetail(userId, 1, detailPageSize);
  };

  const onSearchUsers = (keyword: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = keyword.trim();
    if (!q) {
      setUserOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setUserSearching(true);
      try {
        const users = await searchUsers(q);
        setUserOptions(users);
        mergeUsers(users);
      } catch (err) {
        message.error(err instanceof Error ? err.message : '搜索用户失败');
      } finally {
        setUserSearching(false);
      }
    }, 300);
  };

  const columns: ColumnsType<UsageByUserRow> = useMemo(
    () => [
      {
        title: '用户',
        dataIndex: 'userId',
        key: 'user',
        render: (userId: string) => {
          const u = userCache.get(userId);
          return (
            <div>
              <div>{displayName(u, userId)}</div>
              {u?.email ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {u.email}
                </Text>
              ) : null}
            </div>
          );
        },
      },
      {
        title: 'userId',
        dataIndex: 'userId',
        key: 'userId',
        ellipsis: true,
        width: 280,
        render: (id: string) => <Text copyable={{ text: id }}>{id}</Text>,
      },
      {
        title: 'Input',
        dataIndex: 'inputTokens',
        key: 'inputTokens',
        align: 'right',
        width: 120,
        render: (v: number) => (
          <span title={formatTokensExact(v)}>{formatTokensCompact(v)}</span>
        ),
      },
      {
        title: 'Output',
        dataIndex: 'outputTokens',
        key: 'outputTokens',
        align: 'right',
        width: 120,
        render: (v: number) => (
          <span title={formatTokensExact(v)}>{formatTokensCompact(v)}</span>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'totalTokens',
        key: 'totalTokens',
        align: 'right',
        width: 120,
        render: (v: number) => (
          <span title={formatTokensExact(v)}>{formatTokensCompact(v)}</span>
        ),
      },
      {
        title: '记录数',
        dataIndex: 'recordCount',
        key: 'recordCount',
        align: 'right',
        width: 100,
      },
    ],
    [userCache],
  );

  const detailColumns: ColumnsType<AdminUsageRecord> = [
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'biz',
      dataIndex: 'bizCode',
      key: 'bizCode',
      width: 100,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '模型',
      key: 'model',
      ellipsis: true,
      render: (_, row) => `${row.providerId}/${row.modelId}`,
    },
    {
      title: 'Input',
      dataIndex: 'inputTokens',
      key: 'inputTokens',
      align: 'right',
      width: 100,
      render: (v: number) => formatTokensExact(v),
    },
    {
      title: 'Output',
      dataIndex: 'outputTokens',
      key: 'outputTokens',
      align: 'right',
      width: 100,
      render: (v: number) => formatTokensExact(v),
    },
    {
      title: 'Total',
      dataIndex: 'totalTokens',
      key: 'totalTokens',
      align: 'right',
      width: 100,
      render: (v: number) => formatTokensExact(v),
    },
  ];

  const onTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 50);
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>
        AI 用量
      </Title>
      <Space wrap style={{ marginBottom: 16 }}>
        <RangePicker
          value={range}
          allowClear={false}
          onChange={(values) => {
            if (values?.[0] && values[1]) {
              setPage(1);
              setRange([values[0], values[1]]);
            }
          }}
        />
        <Select
          allowClear
          showSearch
          placeholder="bizCode（全部）"
          style={{ width: 180 }}
          options={BIZ_OPTIONS}
          value={bizCode}
          onChange={(v) => {
            setPage(1);
            setBizCode(v);
          }}
        />
        <Select
          allowClear
          showSearch
          filterOption={false}
          placeholder="搜索用户（邮箱/用户名）"
          style={{ width: 280 }}
          loading={userSearching}
          value={filterUserId}
          options={userOptions.map((u) => ({
            value: u.id,
            label: `${displayName(u, u.id)} (${u.email || u.username})`,
          }))}
          onSearch={onSearchUsers}
          onChange={(v) => {
            setPage(1);
            setFilterUserId(v);
          }}
        />
      </Space>

      <Table<UsageByUserRow>
        rowKey="userId"
        loading={loading}
        columns={columns}
        dataSource={rows}
        onRow={(record) => ({
          onClick: () => openDetail(record.userId),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (n) => `共 ${n} 个用户`,
        }}
        onChange={onTableChange}
      />

      <Drawer
        title={
          detailUserId
            ? `用量明细 · ${displayName(userCache.get(detailUserId), detailUserId)}`
            : '用量明细'
        }
        width={900}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <Table<AdminUsageRecord>
          rowKey="id"
          loading={detailLoading}
          columns={detailColumns}
          dataSource={detailRows}
          size="small"
          pagination={{
            current: detailPage,
            pageSize: detailPageSize,
            total: detailTotal,
            showSizeChanger: true,
          }}
          onChange={(pagination) => {
            const nextPage = pagination.current ?? 1;
            const nextSize = pagination.pageSize ?? 50;
            setDetailPage(nextPage);
            setDetailPageSize(nextSize);
            if (detailUserId) void loadDetail(detailUserId, nextPage, nextSize);
          }}
        />
      </Drawer>
    </div>
  );
}
